import { ref } from 'vue';
import type {
  AutocompleteResultEntry,
  GoogleMapsAutocompleteApiResponse,
  GoogleMapsGeocodeApiResponse,
  LocationResult,
} from '@seb-platform/shared/types/google-maps';
import type { EMMapTranslations } from '../components/storyblok/extra/SharedEMMapBase/SharedEMMap.types';

interface LocationResultSuccess extends LocationResult {
  status: 'success';
}

interface LocationResultNotFound {
  status: 'not_found';
  message: string;
  location: null;
}

export type LocationResultResponse = LocationResultSuccess | LocationResultNotFound;

interface RequestPlacesAutocompleteSuccess {
  status: 'success';
  suggestions: AutocompleteResultEntry[];
}

interface RequestPlacesAutocompleteNotFound {
  status: 'not_found';
  message: string;
}

export type RequestPlacesAutocompleteResponse = RequestPlacesAutocompleteSuccess | RequestPlacesAutocompleteNotFound;

export function useGoogleMaps({ translations }: { translations: EMMapTranslations }) {
  const isLoading = ref(false);
  const autocompleteError = ref('');

  async function getLocationByQueryOrPlaceId(
    req: { query: string } | { placeId: string }
  ): Promise<LocationResultResponse> {
    isLoading.value = true;
    const { status, data } = await $fetch<GoogleMapsGeocodeApiResponse>('/api/google-maps/geocode', {
      params: { ...req },
    }).catch(err => {
      console.log(`Error fetching location for query / place "${'query' in req ? req.query : req.placeId}":`, err);
      return { status: 'not_found', data: null } as GoogleMapsGeocodeApiResponse;
    });
    isLoading.value = false;
    if (status === 'success') {
      autocompleteError.value = '';
      return { status, location: data.location, countryCode: data.countryCode };
    } else {
      autocompleteError.value = translations.locationNotFound;
      return { status, message: translations.locationNotFound, location: null };
    }
  }

  async function getPlacesAutocomplete(query: string): Promise<RequestPlacesAutocompleteResponse> {
    isLoading.value = true;
    const { data } = await $fetch<GoogleMapsAutocompleteApiResponse>('/api/google-maps/autocomplete', {
      params: { query: query },
    }).catch(err => {
      console.log(`Error fetching location for query "${query}":`, err);
      return { status: 'not_found', data: null } as GoogleMapsAutocompleteApiResponse;
    });
    isLoading.value = false;
    if (data) {
      autocompleteError.value = '';
      return { status: 'success', suggestions: data.suggestions };
    } else {
      autocompleteError.value = translations.locationNotFound;
      return { status: 'not_found', message: translations.locationNotFound };
    }
  }

  return {
    getLocationByQueryOrPlaceId,
    getPlacesAutocomplete,
    isLoading,
    autocompleteError,
  };
}
